import { useEffect, useState } from "react";
import api from "../../utils/axiosConfig";
import { motion } from "framer-motion";

const MesCommandes = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(1);



  // Inject styles
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    return () => {
      clearTimeout(timer);
      setIsMounted(false);
      if (styleSheet.parentNode) {
        styleSheet.parentNode.removeChild(styleSheet);
      }
    };
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await api.get(`/api/orders/me`);
        setOrders(res.data);
      } catch (err) {
        console.error("Erreur récupération commandes :", err);
        setError("Impossible de charger les commandes.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Filter orders based on status
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(order => 
        order.status?.toLowerCase() === statusFilter.toLowerCase()
      );
      setFilteredOrders(filtered);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [orders, statusFilter]);

  // Get current orders for pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const handleOrdersPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    setOrdersPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  function StatusIcon({ status }) {
    switch ((status || "").toLowerCase()) {
      case "livrée":
        return <span>✔️</span>;
      case "en cours":
        return <span>⏳</span>;
      case "en attente":
        return <span>⌛</span>;
      case "expédiée":
        return <span>📦</span>;
      case "annulée":
        return <span>❌</span>;
      default:
        return <span>🔵</span>;
    }
  }

  if (!isMounted) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%)',
        fontFamily: "'Cormorant Garamond', serif"
      }}>
        <div style={{
          textAlign: 'center',
          color: '#2c1810'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(212, 175, 55, 0.2)',
            borderTop: '3px solid #d4af37',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ fontSize: '1.2rem', fontWeight: '600' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: 'linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%)',
      fontFamily: "'Cormorant Garamond', serif"
    }}>
      <div style={{
        textAlign: 'center',
        color: '#2c1810'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(212, 175, 55, 0.2)',
          borderTop: '3px solid #d4af37',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{ fontSize: '1.2rem', fontWeight: '600' }}>Chargement des commandes...</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{
      color: '#e53e3e',
      textAlign: 'center',
      background: 'rgba(244, 67, 54, 0.1)',
      borderLeft: '5px solid #e53e3e',
      padding: '1rem',
      borderRadius: '10px',
      margin: '2rem auto',
      maxWidth: '500px',
      fontFamily: "'Cormorant Garamond', serif"
    }}>
      {error}
    </div>
  );

  if (filteredOrders.length === 0 && !loading && statusFilter === 'all')
    return (
      <div style={{
        textAlign: 'center',
        color: '#5a4a3a',
        fontSize: '1.1rem',
        fontFamily: "'Cormorant Garamond', serif",
        margin: '2rem auto',
        maxWidth: '500px'
      }}>
        Vous n'avez pas encore passé de commande.
      </div>
    );

  return (
    <div className="commandes-container">

      {/* Header Section */}
      <motion.div 
        className="commandes-header-section"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2>Mes <span className="commandes-highlight">commandes</span></h2>
        <p style={{
          color: '#5a4a3a',
          fontSize: '1.1rem',
          textAlign: 'center',
          marginTop: '15px',
          fontStyle: 'italic',
          lineHeight: '1.5'
        }}>
          Retrouvez l'historique de vos achats et suivez l'état de vos commandes
        </p>
      </motion.div>

      {/* Main Content */}
      <section className="commandes-content">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="commandes-grid"
        >
          {/* Filters Section - Desktop */}
          <motion.div 
            className="commandes-filters-section"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 style={{
              color: '#2c1810',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '20px',
              fontFamily: "'Cormorant Garamond', serif"
            }}>
              Filtrer par statut
            </h3>
            
            <div className="filters-grid">
              {['all', 'en attente', 'en cours', 'expédiée', 'livrée', 'annulée'].map((status) => (
                <motion.button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`filter-button ${statusFilter === status ? 'active' : ''}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '20px',
                    border: `1px solid ${statusFilter === status ? 'rgba(212, 175, 55, 0.4)' : 'rgba(212, 175, 55, 0.2)'}`,
                    background: statusFilter === status ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: statusFilter === status ? '#2c1810' : '#5a4a3a',
                    fontWeight: statusFilter === status ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '0.9rem',
                    fontFamily: "'Cormorant Garamond', serif"
                  }}
                >
                  {status === 'all' ? 'Toutes' : status.charAt(0).toUpperCase() + status.slice(1)}
                </motion.button>
              ))}
            </div>

            {/* Pagination Controls */}
            <div style={{
              marginTop: '25px',
              textAlign: 'center',
              color: '#5a4a3a',
              fontSize: '0.9rem'
            }}>
              {filteredOrders.length > 0 && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    marginBottom: '15px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{ fontSize: '0.9rem' }}>Éléments par page :</span>
                    <select
                      value={ordersPerPage}
                      onChange={handleOrdersPerPageChange}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '10px',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#2c1810',
                        fontSize: '0.9rem',
                        fontFamily: "'Cormorant Garamond', serif",
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      <option value={1}>1</option>
                      <option value={3}>3</option>
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                  </div>
                  <p>
                    Affichage {indexOfFirstOrder + 1}-{Math.min(indexOfLastOrder, filteredOrders.length)} 
                    sur {filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''}
                  </p>
                </>
              )}
            </div>
          </motion.div>

                      {/* Orders List */}
            <motion.div 
              className="commandes-list-section"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {filteredOrders.length === 0 && statusFilter !== 'all' ? (
                <div style={{
                  textAlign: 'center',
                  color: '#5a4a3a',
                  fontSize: '1.1rem',
                  fontFamily: "'Cormorant Garamond', serif",
                  padding: '40px 20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '20px',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}>
                  Aucune commande avec le statut "{statusFilter}".
                  <br />
                  <span style={{ fontSize: '0.9rem', fontStyle: 'italic', marginTop: '10px', display: 'block' }}>
                    Essayez un autre filtre ou revenez à "Toutes".
                  </span>
                </div>
              ) : (
                currentOrders.map((order, orderIndex) => (
              <motion.div
                key={order._id}
                className="commande-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.5, delay: orderIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="commande-header">
                  <h3 style={{
                    color: '#2c1810',
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    marginBottom: '15px',
                    fontFamily: "'Cormorant Garamond', serif"
                  }}>
                    Commande #{order._id.slice(-8)}
                  </h3>
                  
                  <div className="status-wrapper" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 16px',
                    borderRadius: '25px',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    background: (() => {
                      switch (order.status?.toLowerCase()) {
                        case "livrée": return "rgba(76, 175, 80, 0.1)";
                        case "en cours": return "rgba(255, 152, 0, 0.1)";
                        case "en attente": return "rgba(255, 193, 7, 0.1)";
                        case "expédiée": return "rgba(156, 39, 176, 0.1)";
                        case "annulée": return "rgba(244, 67, 54, 0.1)";
                        default: return "rgba(33, 150, 243, 0.1)";
                      }
                    })(),
                    color: (() => {
                      switch (order.status?.toLowerCase()) {
                        case "livrée": return "#2e7d32";
                        case "en cours": return "#f57c00";
                        case "en attente": return "#f9a825";
                        case "expédiée": return "#7b1fa2";
                        case "annulée": return "#c62828";
                        default: return "#1976d2";
                      }
                    })(),
                    border: `1px solid ${(() => {
                      switch (order.status?.toLowerCase()) {
                        case "livrée": return "rgba(76, 175, 80, 0.3)";
                        case "en cours": return "rgba(255, 152, 0, 0.3)";
                        case "en attente": return "rgba(255, 193, 7, 0.3)";
                        case "expédiée": return "rgba(156, 39, 176, 0.3)";
                        case "annulée": return "rgba(244, 67, 54, 0.3)";
                        default: return "rgba(33, 150, 243, 0.3)";
                      }
                    })()}`
                  }}>
                    <StatusIcon status={order.status} />
                    {order.status}
                  </div>
                </div>

                <div className="commande-items">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="item-card">
                      <div className="item-header">
                        <h4 style={{
                          color: '#2c1810',
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          margin: '0 0 10px 0',
                          fontFamily: "'Cormorant Garamond', serif"
                        }}>
                          {item.product?.name || "Produit supprimé"}
                        </h4>
                        <span style={{
                          background: 'rgba(212, 175, 55, 0.2)',
                          color: '#d4af37',
                          padding: '4px 12px',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          border: '1px solid rgba(212, 175, 55, 0.4)'
                        }}>
                          {item.quantity}x
                        </span>
                      </div>
                      
                      <div className="item-details">
                        <div className="detail-row">
                          <span style={{ color: '#5a4a3a', fontSize: '0.9rem' }}>Couleur</span>
                          <span style={{ color: '#2c1810', fontWeight: '600', fontSize: '0.9rem' }}>
                            {item.couleur || "N/A"}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span style={{ color: '#5a4a3a', fontSize: '0.9rem' }}>Taille</span>
                          <span style={{ color: '#2c1810', fontWeight: '600', fontSize: '0.9rem' }}>
                            {item.taille || "N/A"}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span style={{ color: '#5a4a3a', fontSize: '0.9rem' }}>Prix unitaire</span>
                          <span style={{ color: '#d4af37', fontWeight: '700', fontSize: '1rem' }}>
                            {item.price} MAD
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Promo Info */}
          {order.promoCode && order.promoData?.pourcentage && (
                  <div style={{
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    borderRadius: '15px',
                    padding: '12px 16px',
                    margin: '15px 0',
                    textAlign: 'center'
                  }}>
                    <span style={{
                      color: '#2e7d32',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}>
                      ✅ Promo « {order.promoCode} » appliquée ({order.promoData.pourcentage}% de réduction)
                    </span>
                  </div>
                )}

                {/* Total */}
                <div style={{
                  textAlign: 'right',
                  marginTop: '20px',
                  paddingTop: '15px',
                  borderTop: '1px solid rgba(212, 175, 55, 0.2)'
                }}>
                  <span style={{
                    color: '#d4af37',
                    fontWeight: '700',
                    fontSize: '1.2rem'
                  }}>
                    Total : {order.total.toFixed(2)} MAD
                  </span>
                </div>
              </motion.div>
            ))
          )}

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div 
                className="pagination-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '30px',
                  flexWrap: 'wrap'
                }}
              >
                <motion.button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '15px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    background: currentPage === 1 ? 'rgba(212, 175, 55, 0.1)' : 'rgba(212, 175, 55, 0.2)',
                    color: currentPage === 1 ? '#5a4a3a' : '#2c1810',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '0.9rem',
                    fontFamily: "'Cormorant Garamond', serif",
                    opacity: currentPage === 1 ? 0.5 : 1
                  }}
                >
                  Précédent
                </motion.button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <motion.button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '12px',
                      border: `1px solid ${currentPage === page ? 'rgba(212, 175, 55, 0.4)' : 'rgba(212, 175, 55, 0.2)'}`,
                      background: currentPage === page ? 'rgba(212, 175, 55, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                      color: currentPage === page ? '#2c1810' : '#5a4a3a',
                      fontWeight: currentPage === page ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem',
                      fontFamily: "'Cormorant Garamond', serif",
                      minWidth: '35px'
                    }}
                  >
                    {page}
                  </motion.button>
                ))}

                <motion.button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '15px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    background: currentPage === totalPages ? 'rgba(212, 175, 55, 0.1)' : 'rgba(212, 175, 55, 0.2)',
                    color: currentPage === totalPages ? '#5a4a3a' : '#2c1810',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '0.9rem',
                    fontFamily: "'Cormorant Garamond', serif",
                    opacity: currentPage === totalPages ? 0.5 : 1
                  }}
                >
                  Suivant
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default MesCommandes;

// CSS liquid glass élégant et traditionnel inspiré de Contact.jsx
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Cormorant+Garamond:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  font-family: 'Cormorant Garamond', serif;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
}

.commandes-container {
  font-family: 'Cormorant Garamond', serif;
  color: #2c1810;
  margin: 0 auto;
  overflow-x: hidden;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
}

/* Main Content */
.commandes-content {
  margin: 0 auto;
  padding: 60px 30px;
  position: relative;
  z-index: 2;
}

.commandes-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 60px;
  align-items: start;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header Section */
.commandes-header-section {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 20px;
  padding: 40px 35px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  text-align: center;
}

.commandes-header-section h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.commandes-highlight {
  position: relative;
  color: #d4af37;
}

.commandes-highlight::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 8px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
  border-radius: 4px;
}

/* Filters Section */
.commandes-filters-section {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 20px;
  padding: 30px 25px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  height: fit-content;
  position: sticky;
  top: 20px;
}

.filters-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-button {
  text-align: left;
  width: 100%;
}

/* Orders List Section */
.commandes-list-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.commande-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 20px;
  padding: 30px 25px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.commande-card:hover {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 12px 40px rgba(212, 175, 55, 0.2);
  transform: translateY(-2px);
  border: 1px solid rgba(212, 175, 55, 0.3);
}

.commande-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.commande-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.item-card {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.item-card:hover {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(212, 175, 55, 0.3);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .commandes-grid {
    gap: 40px;
  }
  
  .commandes-header-section {
    padding: 35px 30px;
  }
  
  .commande-card {
    padding: 25px 20px;
  }
}

@media (max-width: 768px) {
  .commandes-content {
    padding: 50px 20px;
  }
  
  .commandes-grid {
    grid-template-columns: 1fr;
    gap: 35px;
  }
  
  .commandes-header-section {
    padding: 30px 25px;
  }
  
  .commandes-header-section h2 {
    font-size: 2rem;
  }
  
  .commandes-filters-section {
    position: static;
    margin-bottom: 25px;
  }
  
  .filters-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .filter-button {
    text-align: center;
    width: auto;
  }
  
  .commande-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .status-wrapper {
    align-self: flex-start;
  }
  
  .item-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .commandes-content {
    padding: 40px 15px;
  }
  
  .commandes-header-section,
  .commande-card {
    padding: 25px 20px;
  }
  
  .item-card {
    padding: 18px;
  }
  
  .commandes-header-section h2 {
    font-size: 1.8rem;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
